import RingGroupsService from '@/services/RingGroupsService'

function str(v) {
  return String(v ?? '').trim()
}

function normalizeRingGroup(rg) {
  if (!rg || typeof rg !== 'object') return null

  return {
    id: str(rg.id),
    rg_name: str(rg.rg_name),
    name: str(rg.name),

    overflow: {
      timeout: rg?.overflow?.timeout ?? null,
      rollTo: rg?.overflow?.rollTo ?? null
    },

    members: Array.isArray(rg.members) ? rg.members.map(m => ({
      id: Number.isFinite(m.id) ? m.id : null,
      user_id: Number.isFinite(m.user_id) ? m.user_id : null,
      user_name: str(m.user_name),
      sip_user_id: str(m.sip_user_id),
      extension: str(m.extension || m.sip_user_id),
      ring_order: Number.isFinite(m.ring_order) ? m.ring_order : 0,
      enabled: m.enabled !== undefined ? !!m.enabled : true
    })) : []
  }
}

export default {
  namespaced: true,

  state: () => ({
    items: [],
    current: null,
    loading: false,
    saving: false,
    error: null
  }),

  getters: {
    list: (s) => s.items,
    current: (s) => s.current,
    byId: (s) => (id) => s.items.find(x => x.id === str(id)) || null
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_SAVING(state, v) { state.saving = !!v },
    SET_ERROR(state, v) { state.error = v || null },

    SET_LIST(state, rows) { state.items = Array.isArray(rows) ? rows : [] },

    UPSERT_ONE(state, row) {
      const rg = normalizeRingGroup(row)
      if (!rg?.id) return

      const i = state.items.findIndex(x => x.id === rg.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], ...rg })
      else state.items.unshift(rg)

      if (state.current?.id === rg.id) {
        state.current = { ...state.current, ...rg }
      }
    },

    SET_CURRENT(state, row) {
      state.current = row ? normalizeRingGroup(row) : null
    },

    REMOVE_ONE(state, endpointId) {
      const id = str(endpointId)
      state.items = state.items.filter(x => x.id !== id)
      if (state.current?.id === id) state.current = null
    },

    SET_CURRENT_MEMBERS(state, members) {
      if (!state.current) return
      state.current = { ...state.current, members: Array.isArray(members) ? members : [] }

      const i = state.items.findIndex(x => x.id === state.current.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], members: state.current.members })
    }
  },

  actions: {
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await RingGroupsService.list(params)
        const rows = RingGroupsService.unwrapList(res).map(normalizeRingGroup).filter(Boolean)
        commit('SET_LIST', rows)
        return rows
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchOne({ commit }, { id, params = {} }) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing ring group id')

      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await RingGroupsService.get(endpointId, params)
        const row = normalizeRingGroup(RingGroupsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async create({ commit }, payload = {}) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await RingGroupsService.create(payload)
        const row = normalizeRingGroup(RingGroupsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async update({ commit }, { id, payload = {} }) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing ring group id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await RingGroupsService.update(endpointId, payload)
        const row = normalizeRingGroup(RingGroupsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async destroy({ commit }, id) {
      const endpointId = str(id)
      if (!endpointId) throw new Error('Missing ring group id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        await RingGroupsService.destroy(endpointId)
        commit('REMOVE_ONE', endpointId)
        return true
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    async syncMembers({ commit, state }, { id, members }) {
      const endpointId = str(id || state.current?.id)
      if (!endpointId) throw new Error('Missing ring group id')

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await RingGroupsService.syncMembers(endpointId, members || [])
        const row = normalizeRingGroup(RingGroupsService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) {
        commit('SET_ERROR', e)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    }
  }
}
